import type { CrosswordDoc, WordDraft, DeadCell } from './doc';
import { normalizeLetter, normalizeWord } from './normalize';

// Reducer PURO da cruzada direta. Contrato do shell (history.ts):
//   devolve a MESMA referência quando a ação não muda nada — é assim que o undo
//   decide o que empilhar. Toda rejeição é um no-op que retorna `doc` intacto.
//
// A palavra da direta não tem slot pré-cavado: seu tamanho é `texto.length` e as
// letras ocupam x..x+len-1 (horizontal) ou y..y+len-1 (vertical). Palavras PODEM
// cruzar (compartilhar casa de letra — PATO/PE dividem a casa inicial); o que não
// podem é passar por cima de uma casa morta.

export type CrosswordAction =
  | { type: 'ADD_DEAD_CELL'; x: number; y: number }
  | { type: 'REMOVE_DEAD_CELL'; x: number; y: number }
  | { type: 'REMOVE_WORD'; ref: string }
  | { type: 'MOVE_DEAD_CELL'; fromX: number; fromY: number; toX: number; toY: number }
  | { type: 'HANG_WORD'; deadX: number; deadY: number; startX: number; startY: number; direcao: boolean; ref: string }
  | { type: 'TYPE_LETTER'; ref: string; letter: string }
  | { type: 'SET_WORD'; ref: string; texto: string }
  | { type: 'BACKSPACE'; ref: string }
  | { type: 'SET_CLUE'; ref: string; chave: string }
  | { type: 'SET_META'; titulo?: string; tiposDePassatemposId?: number | null };

// ---- geometria ----

function inBounds(doc: CrosswordDoc, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < doc.largura && y < doc.altura;
}

/** Casas ocupadas pela palavra: (x+i,y) horizontal, (x,y+i) vertical. */
function wordCells(w: WordDraft): { x: number; y: number }[] {
  const cells: { x: number; y: number }[] = [];
  for (let i = 0; i < w.texto.length; i++) {
    cells.push(w.direcao ? { x: w.x, y: w.y + i } : { x: w.x + i, y: w.y });
  }
  return cells;
}

function deadAt(doc: CrosswordDoc, x: number, y: number): boolean {
  return doc.deadCells.some((c) => c.x === x && c.y === y);
}

function letterAt(doc: CrosswordDoc, x: number, y: number): boolean {
  return doc.words.some((w) => wordCells(w).some((c) => c.x === x && c.y === y));
}

// ---- ações ----

function addDeadCell(doc: CrosswordDoc, x: number, y: number): CrosswordDoc {
  if (!inBounds(doc, x, y)) return doc;
  if (deadAt(doc, x, y)) return doc;
  if (letterAt(doc, x, y)) return doc; // casa morta não senta em cima de letra (§7.2)
  const cell: DeadCell = { x, y, visivel: true, palavra1Ref: null, palavra2Ref: null };
  return { ...doc, deadCells: [...doc.deadCells, cell] };
}

/** Remove a casa de dica e as palavras penduradas nela (elas pertencem a essa dica). */
function removeDeadCell(doc: CrosswordDoc, x: number, y: number): CrosswordDoc {
  const cell = doc.deadCells.find((c) => c.x === x && c.y === y);
  if (!cell) return doc;
  const refs = new Set(
    [cell.palavra1Ref, cell.palavra2Ref].filter((r): r is string => r != null),
  );
  return {
    ...doc,
    deadCells: doc.deadCells.filter((c) => c !== cell),
    words: doc.words.filter((w) => !refs.has(w.ref)),
  };
}

/** Remove uma palavra e a desliga do slot de qualquer casa de dica. */
function removeWord(doc: CrosswordDoc, ref: string): CrosswordDoc {
  if (!doc.words.some((w) => w.ref === ref)) return doc;
  return {
    ...doc,
    words: doc.words.filter((w) => w.ref !== ref),
    deadCells: doc.deadCells.map((c) =>
      c.palavra1Ref === ref || c.palavra2Ref === ref
        ? {
            ...c,
            palavra1Ref: c.palavra1Ref === ref ? null : c.palavra1Ref,
            palavra2Ref: c.palavra2Ref === ref ? null : c.palavra2Ref,
          }
        : c,
    ),
  };
}

function hangWord(
  doc: CrosswordDoc,
  deadX: number,
  deadY: number,
  startX: number,
  startY: number,
  direcao: boolean,
  ref: string,
): CrosswordDoc {
  const cell = doc.deadCells.find((c) => c.x === deadX && c.y === deadY);
  if (!cell) return doc;
  if (doc.words.some((w) => w.ref === ref)) return doc; // ref já usado
  if (cell.palavra1Ref != null && cell.palavra2Ref != null) return doc; // dois slots cheios

  // A palavra começa imediatamente à direita OU imediatamente abaixo da casa morta
  // (convenção clássica da direta — §8.4). Qualquer outra colocação é recusada.
  const isRight = startX === deadX + 1 && startY === deadY;
  const isBelow = startX === deadX && startY === deadY + 1;
  if (!isRight && !isBelow) return doc;
  if (!inBounds(doc, startX, startY)) return doc; // não cabe nem a 1ª letra
  if (deadAt(doc, startX, startY)) return doc; // início cairia numa casa morta

  // A direção já pendurada nesta casa não pode repetir (uma H e uma V, no máximo).
  const slotRefs = [cell.palavra1Ref, cell.palavra2Ref].filter((r): r is string => r != null);
  const taken = slotRefs.some((r) => doc.words.find((w) => w.ref === r)?.direcao === direcao);
  if (taken) return doc;

  const word: WordDraft = { ref, texto: '', x: startX, y: startY, direcao, chave: '' };
  const slot = cell.palavra1Ref == null ? 'palavra1Ref' : 'palavra2Ref';
  const deadCells = doc.deadCells.map((c) => (c === cell ? { ...c, [slot]: ref } : c));
  return { ...doc, words: [...doc.words, word], deadCells };
}

function typeLetter(doc: CrosswordDoc, ref: string, letter: string): CrosswordDoc {
  const word = doc.words.find((w) => w.ref === ref);
  if (!word) return doc;
  const ch = normalizeLetter(letter);
  if (ch.length !== 1) return doc; // só uma letra normalizada por vez

  const nextLen = word.texto.length + 1;
  const nx = word.direcao ? word.x : word.x + nextLen - 1;
  const ny = word.direcao ? word.y + nextLen - 1 : word.y;
  if (!inBounds(doc, nx, ny)) return doc; // estouraria a grade
  if (deadAt(doc, nx, ny)) return doc; // passaria por cima de casa morta

  const words = doc.words.map((w) => (w === word ? { ...w, texto: w.texto + ch } : w));
  return { ...doc, words };
}

/** Define a palavra inteira de uma vez (o campo de texto do Inspector). Normaliza,
 *  descarta o que não é letra, e recusa (no-op) se estourar a grade ou cair numa casa
 *  de dica — igual ao TYPE_LETTER, mas para o texto completo. */
function setWord(doc: CrosswordDoc, ref: string, texto: string): CrosswordDoc {
  const word = doc.words.find((w) => w.ref === ref);
  if (!word) return doc;
  const clean = normalizeWord(texto).replace(/[^A-Z0-9]/g, '');
  if (clean === word.texto) return doc;
  for (let i = 0; i < clean.length; i++) {
    const x = word.direcao ? word.x : word.x + i;
    const y = word.direcao ? word.y + i : word.y;
    if (!inBounds(doc, x, y)) return doc;
    if (deadAt(doc, x, y)) return doc;
  }
  const words = doc.words.map((w) => (w === word ? { ...w, texto: clean } : w));
  return { ...doc, words };
}

function backspace(doc: CrosswordDoc, ref: string): CrosswordDoc {
  const word = doc.words.find((w) => w.ref === ref);
  if (!word || word.texto.length === 0) return doc;
  const words = doc.words.map((w) => (w === word ? { ...w, texto: w.texto.slice(0, -1) } : w));
  return { ...doc, words };
}

function setClue(doc: CrosswordDoc, ref: string, chave: string): CrosswordDoc {
  const word = doc.words.find((w) => w.ref === ref);
  if (!word || word.chave === chave) return doc;
  const words = doc.words.map((w) => (w === word ? { ...w, chave } : w));
  return { ...doc, words };
}

function setMeta(
  doc: CrosswordDoc,
  patch: { titulo?: string; tiposDePassatemposId?: number | null },
): CrosswordDoc {
  const nextTitulo = patch.titulo ?? doc.titulo;
  const nextTipo =
    patch.tiposDePassatemposId !== undefined ? patch.tiposDePassatemposId : doc.tiposDePassatemposId;
  if (nextTitulo === doc.titulo && nextTipo === doc.tiposDePassatemposId) return doc;
  return { ...doc, titulo: nextTitulo, tiposDePassatemposId: nextTipo };
}

function moveDeadCell(
  doc: CrosswordDoc,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): CrosswordDoc {
  const cell = doc.deadCells.find((c) => c.x === fromX && c.y === fromY);
  if (!cell) return doc;
  if (!inBounds(doc, toX, toY)) return doc;
  if (fromX === toX && fromY === toY) return doc;
  if (deadAt(doc, toX, toY)) return doc; // destino já é casa morta

  const dx = toX - fromX;
  const dy = toY - fromY;
  const hung = new Set([cell.palavra1Ref, cell.palavra2Ref].filter((r): r is string => r != null));

  // Toda casa das palavras penduradas anda pelo delta; tudo tem que continuar na grade.
  const movedWords = doc.words.map((w) => (hung.has(w.ref) ? { ...w, x: w.x + dx, y: w.y + dy } : w));
  for (const w of movedWords) {
    if (!hung.has(w.ref)) continue;
    for (const c of wordCells(w)) {
      if (!inBounds(doc, c.x, c.y)) return doc;
    }
  }

  const deadCells = doc.deadCells.map((c) => (c === cell ? { ...c, x: toX, y: toY } : c));
  return { ...doc, words: movedWords, deadCells };
}

export function crosswordReducer(doc: CrosswordDoc, action: CrosswordAction): CrosswordDoc {
  switch (action.type) {
    case 'ADD_DEAD_CELL':
      return addDeadCell(doc, action.x, action.y);
    case 'REMOVE_DEAD_CELL':
      return removeDeadCell(doc, action.x, action.y);
    case 'REMOVE_WORD':
      return removeWord(doc, action.ref);
    case 'MOVE_DEAD_CELL':
      return moveDeadCell(doc, action.fromX, action.fromY, action.toX, action.toY);
    case 'HANG_WORD':
      return hangWord(
        doc,
        action.deadX,
        action.deadY,
        action.startX,
        action.startY,
        action.direcao,
        action.ref,
      );
    case 'TYPE_LETTER':
      return typeLetter(doc, action.ref, action.letter);
    case 'SET_WORD':
      return setWord(doc, action.ref, action.texto);
    case 'BACKSPACE':
      return backspace(doc, action.ref);
    case 'SET_CLUE':
      return setClue(doc, action.ref, action.chave);
    case 'SET_META':
      return setMeta(doc, action);
    default:
      return doc;
  }
}
